import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Button,
  Divider,
  Collapse,
  IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { performUnifiedSearch, SEARCH_RESULT_TYPES } from '../services/searchService';
import { getTypeIcon, getTypeColor, getTypeLabel, formatDate } from '../utils/searchUtils';

/**
 * Comprehensive search results page showing all matches
 */
const SearchResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [allResults, setAllResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedSections, setExpandedSections] = useState({});

  // Get initial search query from URL params or location state
  useEffect(() => {
    const queryFromParams = searchParams.get('q');
    const queryFromState = location.state?.searchQuery;
    const initialQuery = queryFromParams || queryFromState || '';
    
    if (initialQuery) {
      setSearchQuery(initialQuery);
      performFullSearch(initialQuery);
    }
  }, [searchParams, location.state]);

  // Perform comprehensive search without limits
  const performFullSearch = async (query) => {
    if (!query || query.trim().length < 2) {
      setAllResults([]);
      setTotalCount(0);
      return;
    }

    setLoading(true);
    try {
      // Get all results without limit
      const results = await performUnifiedSearch(query, 1000);
      setAllResults(results);
      setTotalCount(results.length);
    } catch (error) {
      console.error('Error performing full search:', error);
      setAllResults([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle new search from this page
  const handleSearch = (event) => {
    if (event.key === 'Enter') {
      performFullSearch(searchQuery);
      // Update URL to reflect new search
      navigate(`/search-results?q=${encodeURIComponent(searchQuery)}`, { replace: true });
    }
  };

  // Handle result selection
  const handleResultClick = (result) => {
    if (result.type === SEARCH_RESULT_TYPES.JOURNAL_PDF_CONTENT) {
      // Navigate to PDF viewer with search context
      navigate('/benny-journal/pdf-viewer', {
        state: {
          pdfUrl: result.pdfUrl,
          searchPhrase: searchQuery,
          matchNumber: result.matchNumber,
          referrer: `/search-results?q=${encodeURIComponent(searchQuery)}`
        }
      });
    } else if (result.navigationPath) {
      navigate(result.navigationPath);
    }
  };

  // Toggle section expansion
  const toggleSection = (type) => {
    setExpandedSections(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Group results by type for better organization
  const groupedResults = allResults.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {});

  // Initialize expanded sections when results change
  React.useEffect(() => {
    if (allResults.length > 0) {
      const types = [...new Set(allResults.map(result => result.type))];
      const initialExpanded = {};
      types.forEach(type => {
        initialExpanded[type] = true; // Start with all sections expanded
      });
      setExpandedSections(initialExpanded);
    }
  }, [allResults]);

  const getTypeName = (type) => {
    const typeNames = {
      [SEARCH_RESULT_TYPES.CAMPAIGN_EPISODE]: 'Campaign Episodes',
      [SEARCH_RESULT_TYPES.POST]: 'Posts',
      [SEARCH_RESULT_TYPES.MAP]: 'Maps',
      [SEARCH_RESULT_TYPES.PIN]: 'Map Pins',
      [SEARCH_RESULT_TYPES.ACCOLADE]: 'Accolades',
      [SEARCH_RESULT_TYPES.JOURNAL]: 'Journal Entries',
      [SEARCH_RESULT_TYPES.JOURNAL_PDF_CONTENT]: "Benny's Journal",
      [SEARCH_RESULT_TYPES.WIKI]: 'Wiki'
    };
    return typeNames[type] || getTypeLabel(type);
  };

  return (
    <Box sx={{ backgroundColor: '#1c1c1c', minHeight: '100vh', py: 4 }}>
      <Container 
        maxWidth="lg" 
        sx={{ 
          backgroundColor: '#F5EFE6',
          borderRadius: 2,
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          p: 4
        }}
      >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ 
            mb: 2,
            color: '#B08D57',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(176, 141, 87, 0.1)',
              color: '#d98d2a'
            }
          }}
        >
          Back
        </Button>
        
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          sx={{ 
            color: '#d98d2a',
            fontWeight: 'bold',
            fontFamily: '"Georgia", serif'
          }}
        >
          Search Results
        </Typography>
        
        {/* Search input */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search across all content..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#B08D57' }} />
              </InputAdornment>
            ),
          }}
          sx={{ 
            mb: 3,
            '& .MuiInputBase-root': {
              backgroundColor: '#E8DCCB',
              color: '#2c2c2c',
              border: '2px solid #D7CCC8',
              borderRadius: '8px',
            },
            '& .MuiInputBase-input': {
              color: '#2c2c2c',
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#8D6E63',
              opacity: 1,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
            '&:hover .MuiInputBase-root': {
              borderColor: '#d98d2a',
            },
            '& .Mui-focused .MuiInputBase-root': {
              borderColor: '#d98d2a',
            }
          }}
        />

        {/* Results summary */}
        {!loading && searchQuery && (
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#5D4037',
              fontWeight: 500
            }} 
            gutterBottom
          >
            {totalCount > 0 
              ? `Found ${totalCount} result${totalCount === 1 ? '' : 's'} for "${searchQuery}"`
              : `No results found for "${searchQuery}"`
            }
          </Typography>
        )}
      </Box>

      {/* Loading indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Results */}
      {!loading && totalCount > 0 && (
        <Box>
          {Object.entries(groupedResults).map(([type, results]) => (
            <Paper 
              key={type} 
              elevation={4}
              sx={{ 
                mb: 3,
                backgroundColor: '#E8DCCB',
                border: '2px solid #B08D57',
                borderRadius: '8px'
              }}
            >
              <Box 
                onClick={() => toggleSection(type)}
                sx={{ 
                  p: 2.5, 
                  borderBottom: `2px solid #D7CCC8`,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  '&:hover': {
                    backgroundColor: '#D7CCC8'
                  }
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: '#5D4037',
                    fontWeight: 700,
                    fontFamily: '"Georgia", serif'
                  }}
                >
                  {React.cloneElement(getTypeIcon(type), { sx: { color: '#d98d2a' } })}
                  {getTypeName(type)} ({results.length})
                </Typography>
                <IconButton size="small" sx={{ color: '#B08D57' }}>
                  {expandedSections[type] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              
              <Collapse in={expandedSections[type]}>
                <List>
                {results.map((result, index) => (
                  <React.Fragment key={`${result.type}-${result.id}-${index}`}>
                    <ListItem
                      button
                      onClick={() => handleResultClick(result)}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        }
                      }}
                    >
                      <ListItemIcon>
                        {getTypeIcon(result.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" component="span">
                              {result.title}
                            </Typography>
                            <Chip
                              label={result.subtitle || getTypeName(result.type)}
                              size="small"
                              sx={{
                                backgroundColor: getTypeColor(result.type),
                                color: 'white',
                                fontSize: '0.7rem',
                                pointerEvents: 'none'
                              }}
                            />
                            {result.matchNumber && (
                              <Chip
                                label={`Match #${result.matchNumber}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', pointerEvents: 'none' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {result.description || result.excerpt || 'No description available'}
                            </Typography>
                            {result.context && (
                              <Typography variant="body2" sx={{ 
                                mt: 1, 
                                p: 1, 
                                backgroundColor: '#f9f9f9', 
                                borderRadius: 1,
                                fontStyle: 'italic'
                              }}>
                                "{result.context}"
                              </Typography>
                            )}
                            {result.createdAt && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                {formatDate(result.createdAt)}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < results.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
                </List>
              </Collapse>
            </Paper>
          ))}
        </Box>
      )}

      {/* No results */}
      {!loading && totalCount === 0 && searchQuery && (
        <Paper 
          elevation={4}
          sx={{ 
            p: 4, 
            textAlign: 'center', 
            backgroundColor: '#E8DCCB',
            border: '2px solid #B08D57',
            borderRadius: '8px'
          }}
        >
          <SearchIcon sx={{ fontSize: 64, color: '#B08D57', mb: 2 }} />
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              color: '#5D4037',
              fontFamily: '"Georgia", serif',
              fontWeight: 600
            }}
          >
            No results found
          </Typography>
          <Typography sx={{ color: '#8D6E63' }}>
            Try adjusting your search terms or check for typos.
          </Typography>
        </Paper>
      )}
      </Container>
    </Box>
  );
};

export default SearchResultsPage;